import { chanceNow, storyMoment } from '../../game/cardStory';
import type { GameSession, SessionSnapshot } from '../../game/session';
import { sideName } from '../../game/sideName';
import type { TabletItem } from './tablet';

/** In-world equivalent of the DOM choices. Uses the same authoritative session methods. */
export function combatItems(
  snapshot: SessionSnapshot,
  session: Pick<GameSession, 'commitCardSupport' | 'respondToMuster' | 'continueCardReveal'>,
  page: number,
  setPage: (page: number) => void,
  /** The tablet text size, `LARGE_TEXT` or 1 (client-tablets-ui:bugs#9). */
  k = 1,
): TabletItem[] | null {
  const { cardClash: clash, muster } = snapshot;
  if (!clash && !muster) return null;
  const clock = muster ? null : (snapshot.cardClock ?? null);
  const seconds = clock ? `${clock.left} s` : '';
  const choices: { label: string; run: () => void }[] = [];
  if (muster) {
    for (const piece of muster.pieces)
      choices.push({ label: `Join: ${piece.name}`, run: () => session.respondToMuster(piece.id) });
    choices.push({ label: 'Pass', run: () => session.respondToMuster(null) });
  } else if (clash?.phase === 'choose' && clash.chooser) {
    for (const card of clash.hand) {
      if (card.water) {
        for (const role of ['pressure', 'protection', 'deception'] as const)
          choices.push({
            label: `${card.name}: ${role}`,
            run: () => session.commitCardSupport(card.id, role),
          });
      } else
        choices.push({
          label: `${card.name}: ${card.role}`,
          run: () => session.commitCardSupport(card.id),
        });
    }
    choices.push({ label: 'No support', run: () => session.commitCardSupport(null) });
  }
  const pages = Math.max(1, Math.ceil(choices.length / 5));
  const current = Math.min(page, pages - 1);
  const items: TabletItem[] = [
    {
      kind: 'text',
      text: muster ? `MUSTER · ${sideName(snapshot, muster.faction)}` : 'CARD COMBAT',
      x: muster || !clock ? 520 : 90,
      y: 95,
      size: 40 * k,
      align: muster || !clock ? 'center' : 'left',
      maxWidth: 860,
    },
  ];
  // The seconds left to choose, where the chooser looks for them (client-tablets-ui:quality#4).
  if (clock && clash?.phase === 'choose')
    items.push({
      kind: 'text',
      text: clock.held ? `Stopped at ${seconds}` : `${seconds} left`,
      x: 950,
      y: 95,
      size: 40 * k,
      align: 'right',
    });
  if (!choices.length) {
    // While the clash is told, the chances as they stand, and the outcome only once it is over.
    const told = storyMoment(clash?.story);
    const lines = clash?.scores?.map((s) => {
      const main = clash.participants.find((p) => p.pieceId === s.pieceId);
      const now = chanceNow(clash.story, s.pieceId) ?? s.probability;
      const chance = now === undefined ? 'Chance unavailable' : `${Math.round(now * 100)}% chance`;
      if (!told.over) return `${main?.name ?? s.faction}: ${chance}`;
      const outcome = s.won === undefined ? 'Result recorded' : s.won ? 'Winner' : 'Defeated';
      return `${main?.name ?? s.faction}: ${outcome} · ${chance}`;
    }) ?? ['Waiting for the others to choose'];
    lines.forEach((text, i) =>
      items.push({ kind: 'text', text, x: 100, y: 175 + i * 61, size: 34 * k, maxWidth: 840 }),
    );
    const below = 175 + lines.length * 61 + 20;
    // The revealed cards wait for the people who fought with them (x-player-journey#3).
    if (clash?.phase === 'reveal' && clash.hold === 'continue')
      items.push({
        kind: 'gem',
        id: 'combat-continue',
        rect: { x: 90, y: below, w: 860, h: 68 },
        gem: 'emerald',
        // Pressed while the clash is told, it shows the result at once.
        label: !told.over ? 'Skip' : clock ? `Continue (${seconds})` : 'Continue',
        fontSize: 34 * k,
        enabled: true,
        onTap: () => session.continueCardReveal(),
      });
    else if (clash?.phase === 'reveal' && clock)
      items.push({
        kind: 'text',
        text:
          clash.hold === 'others'
            ? `Waiting for the others to read the cards · ${seconds}`
            : `The game goes on in ${seconds}`,
        x: 100,
        y: below + 34,
        size: 34 * k,
        maxWidth: 840,
      });
    return items;
  }
  choices.slice(current * 5, current * 5 + 5).forEach((choice, i) =>
    items.push({
      kind: 'gem',
      id: `combat-${current * 5 + i}`,
      rect: { x: 90, y: 140 + i * 80, w: 860, h: 68 },
      gem: 'emerald',
      label: choice.label,
      fontSize: 34 * k,
      enabled: true,
      onTap: choice.run,
    }),
  );
  if (pages > 1)
    items.push({
      kind: 'gem',
      id: 'combat-page',
      rect: { x: 90, y: 560, w: 860, h: 64 },
      gem: 'diamond',
      label: `More choices (${current + 1}/${pages})`,
      enabled: true,
      fontSize: 34 * k,
      onTap: () => setPage((current + 1) % pages),
    });
  return items;
}
